import React from "react"
import { ThemeProvider, useColorScheme } from "@mui/material/styles"
import CssBaseline from "@mui/material/CssBaseline"
import { appTheme } from "@/config/theme"

export type ThemeMode = "light" | "dark"

export interface ThemeContextType {
  mode: ThemeMode
  toggleTheme: () => void
}

/**
 * Wraps the MUI ThemeProvider with CSS-variables + colorSchemes support.
 * Mode is persisted in localStorage under the key "theme" (same key used
 * by the old implementation, so no migration is needed for existing users).
 */
const AppThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ThemeProvider theme={appTheme} defaultMode="system" modeStorageKey="theme" disableTransitionOnChange>
    <CssBaseline enableColorScheme />
    {children}
  </ThemeProvider>
)

/**
 * Drop-in replacement for the old `useAppTheme` hook.
 * Internally delegates to MUI's `useColorScheme` so that all MUI v6
 * components (including those using `theme.applyStyles`) receive the
 * correct resolved mode.
 *
 * Must be called from a component tree wrapped by AppThemeProvider.
 */
const useAppTheme = (): ThemeContextType => {
  const { mode: storedMode, setMode, systemMode } = useColorScheme()
  const resolvedMode: ThemeMode = ((storedMode === "system" ? systemMode : storedMode) ?? "light") as ThemeMode
  const toggleTheme = () => setMode(resolvedMode === "light" ? "dark" : "light")
  return { mode: resolvedMode, toggleTheme }
}

export { AppThemeProvider, useAppTheme }
